import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import accountingReportService, { accountingEndpoint } from '@api/v1/accountingReportService'
import reportJobService from '@api/v1/reportJobService'
import { requireArray } from '@api/v1/readShape'
import useReadState from '@hooks/useReadState'
import useLatestRun from '@hooks/useLatestRun'
import { userFacingError } from '@utils/reportProgress'
import SpecializedReportShell from '@components/reports/SpecializedReportShell'
import SpecializedFilterBar from '@components/reports/SpecializedFilterBar'
import { choiceField, dateRangeField } from '@components/reports/specializedFilterFields'
import ReportBand from '@components/reports/ReportBand'
import ReportViewsMenu from '@components/reports/ReportViewsMenu'
import useSavedReportView from '@components/reports/useSavedReportView'
import { describeFilters, dateRangePart } from '@components/reports/reportFilterSummary'
import AutoReportGrid from '@components/reports/AutoReportGrid'
import { escapeHtml } from '@components/reports/escapeHtml'
import { fmtDisplayDate } from '@utils/displayDate'

const fmtMoney = (v) => {
  const n = Number(v ?? 0)
  if (n === 0) return ''
  const fmt = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return n < 0 ? `($${fmt})` : `$${fmt}`
}
// Himaad UI feedback, 2026-08-27: this page's local unpadded 'M/D/YYYY' copy is
// gone — Statement Date and Reconciled On below come from `fmtDisplayDate`
// (MM/DD/YYYY, src/utils/displayDate.js), the one Report Center display
// formatter, which also removes this page's disagreement with the shared
// autoReportColumns.js formatter it renders alongside. These renders are cb2's
// own (CB hands this endpoint raw JSON, not pre-formatted grid cells). The old
// copy echoed an unparseable value back; the shared formatter returns '' instead,
// on purpose — on a bank-reconciliation history a blank is visibly missing
// information while a foreign-shaped date invites the reader to interpret it
// (displayDate.js). `makeDefaults` below holds WIRE values and stays ISO.

// Explicit column spec for AutoReportGrid (CC-49304) — preserves the original
// fixed columns + money/date formatting. Balances aren't additive, so no sum;
// the Difference cell keeps its red highlight via inline HTML (the grid renders
// cell displayValue as HTML and strips it for filtering/sorting).
// Spec 193: `render` is TEXT and AutoReportGrid escapes it — "Reconciled By" is a
// person's name as typed into CB. Only Difference writes markup, so only it uses
// `renderHtml`, and it escapes what it wraps.
const RECON_COLUMNS = [
  { key: 'statementDate', label: 'Statement Date', align: 'left', render: (r) => fmtDisplayDate(r.statementDate) },
  { key: 'beginningBalance', label: 'Beginning Balance', align: 'right', render: (r) => fmtMoney(r.beginningBalance) },
  { key: 'endingBalance', label: 'Ending Balance', align: 'right', render: (r) => fmtMoney(r.endingBalance) },
  {
    key: 'difference', label: 'Difference', align: 'right',
    renderHtml: (r) => {
      const html = escapeHtml(fmtMoney(r.difference))
      return Math.abs(Number(r.difference || 0)) > 0.01
        ? `<span class="text-red-600 font-medium">${html}</span>`
        : html
    },
  },
  { key: 'reconciledBy', label: 'Reconciled By', align: 'left', render: (r) => r.reconciledByName || r.userName || '' },
  { key: 'reconciledOn', label: 'Reconciled On', align: 'left', render: (r) => fmtDisplayDate(r.reconciledOn || r.dateReconciled) },
]

// A stable identity for "no accounts have been read", so the fields memo and the
// auto-pick effect below don't re-run on every render of an unresolved read.
const NO_ACCOUNTS = []

const makeDefaults = () => ({
  accountId: 0,
  startDate: dayjs().subtract(1, 'year').format('YYYY-MM-DD'),
  endDate: dayjs().format('YYYY-MM-DD'),
})

// Bank Account is the one field on any specialized page whose option list is
// SERVER-loaded, so this page's specs are built per render rather than at module
// scope. `accountId` is an int on the wire, hence the parseInt decode; 0 is the
// "nothing chosen" sentinel the run guard checks.
const buildFields = (bankAccounts) => [
  choiceField({
    key: 'account', label: 'Bank Account', valueKey: 'accountId',
    options: [
      { value: 0, label: 'Select an account…' },
      ...bankAccounts.map((a) => ({
        value: a.value || a.id || a.accountId,
        label: a.text || a.name || a.accountName,
      })),
    ],
    decode: (s) => parseInt(s, 10) || 0,
  }),
  dateRangeField({ key: 'dates', label: 'Date Range', startKey: 'startDate', endKey: 'endDate', omitAll: true }),
]

// CC-50148: reportId/jobId were always PASSED by ReportView; this page just never
// declared them, which is why it had no queue control and no way to reopen a saved
// run. The queue spec is withheld without a reportId (a job record requires one).
// Module-level so useLatestRun's `run` keeps one identity (spec 131).
const runErrorMessage = (err) => err.message || 'Report failed'

const ReconciliationHistoryReport = ({ reportId, name = 'Reconciliation History', summary, jobId }) => {
  /*
   * CC-53132 — held as an object, not destructured to `data: bankAccounts = []`.
   *
   * `isLoading` is `isPending && isFetching`, so it is FALSE for a query
   * react-query has PAUSED (the default `networkMode: 'online'` refuses to fire
   * while `onlineManager` believes the browser is offline): `queryFn` is never
   * called, nothing throws, nothing is logged and `data` stays undefined. The old
   * `= []` default turned that into an empty account list, and an empty account
   * list walked this whole page into a false financial statement — no auto-pick,
   * so `accountId` stayed 0, so the guard in `runReport` returned early, so no
   * reconciliation request was ever made, so the grid printed "No data for the
   * selected filters" and "0 reconciliations" under a picker offering nothing but
   * "Select an account…", with no spinner and no error to contradict it. On a
   * trust or operating account that reads as "this account has never been
   * reconciled".
   */
  const accountsQuery = useQuery({
    queryKey: ['recon-bank-accounts'],
    queryFn: () => accountingReportService.reconciliationHistory.getBankAccounts(),
    staleTime: 60_000,
  })
  const accountsState = useReadState('recon-bank-accounts', accountsQuery)
  const accountsReady = accountsState === 'ready'
  /*
   * The picker maps over this on every render, so it has to be an array — but only
   * a READY read may supply one. Before that it is empty because nothing has been
   * read, which is a different fact from the firm having no bank accounts; the
   * `resultSlot` branches below are what keep the two apart.
   *
   * One hole is left deliberately, a layer down: `getBankAccounts()` still ends in
   * `data?.data || []`, so an unreadable 200 body still arrives here as a ready
   * empty list. Its `run` sibling was hardened with `requireArrayIn` for CC-53052
   * and this one was not — the fix belongs in the service, because by the time the
   * page sees `[]` the distinction is already gone.
   */
  const bankAccounts = accountsReady && Array.isArray(accountsQuery.data) ? accountsQuery.data : NO_ACCOUNTS

  const [filters, setFilters] = useState(makeDefaults)
  // Spec 131: only the NEWEST run may write the rows, the spinner, the error or
  // the documents' criteria — the account picker re-runs and is never disabled
  // while a run is out. Spinner immediately when reopening a saved result.
  const {
    running, error, ran, runKey, run: startRun, invalidate: invalidateRun,
  } = useLatestRun({ initialRunning: !!jobId, errorMessage: runErrorMessage })
  const [savedAsOf, setSavedAsOf] = useState(null)
  // Consume-once: the first run reopens the tray saved job; later runs are fresh.
  const jobRef = useRef(jobId || null)
  // CC-50013 (G3): saved views. The hook carries the grid's page state, so
  // the menu cannot be mounted without it (the CC-49732 defect).
  const view = useSavedReportView(setFilters)
  // CC-50029: exports state the period they cover.
  const filtersSummary = useMemo(() => describeFilters(dateRangePart(filters.startDate, filters.endDate)), [filters])
  const [rows, setRows] = useState([])

  // Auto-pick first account once loaded
  useEffect(() => {
    if (bankAccounts.length > 0 && !filters.accountId) {
      const first = bankAccounts[0]
      // Pre-existing. Surfaced, not introduced, by spec 131: the React Compiler
      // lint skipped this whole component while its runReport held a
      // try/finally, which now lives in useLatestRun.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFilters((f) => ({ ...f, accountId: first.value || first.id || first.accountId || 0 }))
    }
  }, [bankAccounts, filters.accountId])

  const runReport = useCallback(() => {
    if (!filters.accountId) {
      // No account, nothing to run — silent, as before. Spec 131: a run still in
      // flight for the PREVIOUS account is retired rather than left to land. Not
      // while a reopen is pending: nothing can be in flight before the first run
      // (it consumes jobRef), and the reopen's spinner must stay up while the
      // account list loads.
      if (!jobRef.current) invalidateRun()
      return undefined
    }
    return startRun(async () => {
      const jid = jobRef.current
      jobRef.current = null // consumed — subsequent runs are fresh
      if (jid) {
        // Tray "View report" → reopen the exact saved snapshot + show its run time.
        const [st, r] = await Promise.all([reportJobService.status(jid), reportJobService.result(jid)])
        /*
         * CC-53132 / CC-53052 — the interactive run goes through `requireArrayIn`;
         * this path to the SAME grid did not, so an unreadable saved body became a
         * bank account with no reconciliations. Both wire shapes CB actually sends
         * still pass; only "neither of them" now reaches the error pane.
         *
         * The old expression was `r?.data?.rows || r?.data || []`, which had two
         * outcomes and neither was usable: a MISSING envelope (`{}`, `{data:null}`)
         * fell through to `[]` and rendered as an empty history, and a
         * wrong-SHAPED one fell through to the envelope object itself, which has no
         * `.map` and threw mid-render inside AutoReportGrid. Measured both ways
         * (`ReconciliationHistoryReport.paused.test.jsx`) — the empty-array-is-falsy
         * reading I first wrote here was simply wrong, `[]` is truthy.
         */
        const body = r?.data
        const saved = requireArray(Array.isArray(body) ? body : body?.rows, {
          noun: 'saved reconciliation history', emptyNoun: 'history', entryNoun: 'reconciliation',
        })
        return () => { setSavedAsOf(st?.createdAt || null); setRows(saved) }
      }
      const result = await accountingReportService.reconciliationHistory.run(filters)
      // Spec 131 decision 3: the saved-result banner clears at the COMMIT.
      return () => { setSavedAsOf(null); setRows(result || []) }
    }, { criteria: { filtersSummary } })
  }, [filters, startRun, invalidateRun, filtersSummary])

  // Spec 131: deferred and cancellable, so an effect pass torn down before the
  // timer fires (StrictMode's simulated remount) starts no run.
  useEffect(() => {
    const t = setTimeout(() => { runReport() }, 0)
    return () => clearTimeout(t)
  }, [filters.accountId])  // eslint-disable-line react-hooks/exhaustive-deps

  // CC-50013 G2 / CC-50148: shared metadata-driven bar (see buildFields).
  //
  // The queue spec is withheld without a `reportId` (a job record requires one).
  // Note this page's INTERACTIVE run stays on the direct `api.get` path — unlike
  // its accounting siblings, `reconciliationHistory.run` does not route through
  // runAccountingViaJob — so only the background/queue/schedule paths create jobs.
  // The accountingKey is registered in cb-api's PERUSER_ACCOUNTING allowlist, so a
  // bank reconciliation's result is per-user and never shared.
  const fields = useMemo(() => buildFields(bankAccounts), [bankAccounts])
  const filterBar = (
    <SpecializedFilterBar
      fields={fields}
      filters={filters}
      onFiltersChange={setFilters}
      defaults={makeDefaults()}
      onRun={runReport}
      isLoading={running}
      disabled={!filters.accountId}
      queue={reportId ? {
        reportId,
        name,
        kind: 'accounting',
        accountingKey: 'reconciliation-history',
        endpoint: (f) => accountingEndpoint('/reports/accounting/reconciliation-history', {
          accountId: f.accountId, startDate: f.startDate, endDate: f.endDate,
        }),
        filters: (f) => ({ accountId: f.accountId, startDate: f.startDate, endDate: f.endDate }),
      } : undefined}
      actions={(
        <ReportViewsMenu
          storageKey="report:reconciliation-history"
          currentFilters={filters}
          onApply={view.applyView}
          getPageState={view.getPageState}
        />
      )}
    />
  )

  /*
   * CC-53132 — the grid may only be on screen once the reads behind it have
   * actually happened, because everything it renders is a claim: the row count and
   * "No data for the selected filters" both state that this bank account has no
   * reconciliations in the period.
   *
   * `DataGrid` can already tell an unresolved read from an empty one on its own
   * (`Array.isArray(data)` → an honest `role="alert"` and no count), but nothing
   * here can reach that: this page renders through `AutoReportGrid`, whose
   * `rows = []` default re-normalises an unresolved value before `ReportDataGrid`
   * or `DataGrid` ever sees it, and neither wrapper forwards a resolved flag. So
   * the branch is made here, at the only place that still knows.
   *
   * Ordered failure-first, with `!accountsReady` as the catch-all: every branch is
   * positively gated, so an unhandled state must land somewhere rather than
   * falling through them all and rendering a blank slot.
   */
  let resultSlot
  if (error) {
    // A run that threw. The shell renders the failure above; a grid under it would
    // print "0 reconciliations", which is a count standing on a read that failed.
    resultSlot = null
  } else if (accountsState === 'failed') {
    /* `ReportBand` rather than another bespoke coloured div — spec 092 exists
       because five shipped surfaces each rolled their own. `role="alert"`, not the
       band's polite default: the user is looking at a report that appears to say
       "nothing here" and must not wait for a pause in the screen reader's queue.
       `userFacingError` because CB exception text reaches `error.message` and has
       carried server filesystem paths (CC-49993). It returns '' for an error with no
       message, hence the second fallback — the sentence must not trail off. */
    const detail = userFacingError(accountsQuery.error?.message, 'the request failed.') || 'the request failed.'
    resultSlot = (
      <ReportBand
        variant="warn"
        role="alert"
        action={{ label: 'Try again', onClick: () => accountsQuery.refetch() }}
      >
        Couldn’t load your bank accounts: {detail}{' '}
        No reconciliation history has been read, so this is not an account with nothing reconciled.
      </ReportBand>
    )
  } else if (accountsState === 'blocked') {
    /* Paused, not failed: react-query never sent the request, so there is no error
       to show — and no spinner either, because a paused query never resolves on its
       own and the spinner would sit there for as long as the browser stayed offline. */
    resultSlot = (
      <ReportBand
        variant="warn"
        role="alert"
        action={{ label: 'Try again', onClick: () => accountsQuery.refetch() }}
      >
        Can’t reach the server, so your bank accounts haven’t loaded. This is not an account with no
        reconciliations — nothing has been read yet. Check your connection and try again.
      </ReportBand>
    )
  } else if (!accountsReady) {
    /* `loading`, and `idle` with it. `idle` is unreachable today (this query has no
       `enabled` option), but it is folded in here rather than left to fall through
       — and folding it in is safe precisely because this is an inline slot: the
       filter bar and the page chrome stay live, so nothing can be stranded. */
    resultSlot = (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center text-sm text-gray-500">
        Loading bank accounts…
      </div>
    )
  } else if (bankAccounts.length === 0) {
    // A resolved read that genuinely found nothing. Safe to say so.
    resultSlot = (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center text-sm text-gray-500">
        No bank accounts are available to report on, so there is no reconciliation history to show.
      </div>
    )
  } else if (!filters.accountId) {
    /* Accounts loaded, none selected — either the auto-pick has not committed yet
       or the user chose "Select an account…" back. Nothing has been run for "no
       account", and the previous account's rows must not be shown under one. */
    resultSlot = (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center text-sm text-gray-500">
        Choose a bank account to see its reconciliation history.
      </div>
    )
  } else {
    resultSlot = (
      <AutoReportGrid key={view.gridKey} rows={rows} columns={RECON_COLUMNS} storageKey="report:reconciliation-history" rowNoun="reconciliation" pageStateRef={view.pageStateRef} initialPageState={view.initialPageState} />
    )
  }

  return (
    <SpecializedReportShell
      name={name} summary={summary} running={running} error={error}
      ranCriteria={ran?.criteria ?? null} runKey={runKey}
      filterBar={filterBar} filtersSummary={filtersSummary} savedAsOf={savedAsOf}
    >
      {resultSlot}
    </SpecializedReportShell>
  )
}

export default ReconciliationHistoryReport
